import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLanguageStore } from '../../stores/useLanguageStore';
import { useAuthStore } from '../../stores/useAuthStore';
import apiClient from '../../services/apiClient';

const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'kn', label: 'ಕನ್ನಡ', short: 'ಕ' },
  { code: 'hi', label: 'हिंदी', short: 'हि' },
];

export default function LanguageSelector() {
  const { i18n } = useTranslation();
  const qc = useQueryClient();
  const language = useLanguageStore((s) => s.language);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  async function handleSelect(lang: string) {
    setOpen(false);
    await i18n.changeLanguage(lang);
    sessionStorage.setItem('appLang', lang);
    useLanguageStore.getState().setLanguage(lang);
    if (isAuthenticated) {
      try {
        await apiClient.patch('/users/me', { language_preference: lang.toUpperCase() });
      } catch {
        // API failure must not block the UI language switch
      }
      void qc.invalidateQueries();
    }
  }

  const selected = LANGUAGES.find((l) => l.code === language) ?? LANGUAGES[0];

  return (
    <div ref={ref} className="relative shrink-0 md:w-[120px]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-9 flex items-center justify-between gap-2 rounded-lg border border-border px-3 text-sm text-earth-900 bg-surface focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
      >
        <span className="leading-none md:hidden font-medium">{selected.short}</span>
        <span className="leading-none hidden md:inline">{selected.label}</span>
        <svg className="w-3 h-3 shrink-0 text-earth-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full right-0 z-50 mt-1 w-[120px] rounded-lg border border-border bg-surface shadow-lg overflow-hidden">
          {LANGUAGES.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              onClick={() => handleSelect(code)}
              className={`w-full h-10 flex items-center px-4 text-sm transition-colors ${
                code === language
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-earth-800 hover:bg-primary-50 hover:text-primary-700'
              }`}
            >
              <span className="leading-none">{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
